import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../App";
import { useAuth } from "../App";
import { useSuburb } from "../App";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { Separator } from "../components/ui/separator";
import {
  CalendarIcon,
  Clock,
  Truck,
  Package,
  Store,
  AlertCircle,
  Crown,
  CheckCircle,
  Info,
  CreditCard,
  HelpCircle,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { format, addDays, isBefore, startOfDay } from "date-fns";

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cart, clearCart, sessionId, fetchCart } = useCart();
  const { user, isAuthenticated, isMember, token } = useAuth();
  const { suburbData } = useSuburb();
  
  const [loading, setLoading] = useState(false);
  const [storeHours, setStoreHours] = useState({});
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [paymentError, setPaymentError] = useState(null);
  
  // eWay Transparent Redirect state
  const [ewayData, setEwayData] = useState(null);
  
  // Card payment form (submitted directly to eWay, never to our server)
  const [cardData, setCardData] = useState({
    card_name: "",
    card_number: "",
    card_expiry_month: "",
    card_expiry_year: "",
    card_cvn: "",
  });
  
  const [formData, setFormData] = useState({
    fullName: user?.full_name || "",
    email: user?.email || "",
    phone: "",
    address: "",
    suburb: suburbData?.suburb || "",
    postcode: suburbData?.postcode || "",
    state: suburbData?.state || "",
    deliveryDate: addDays(new Date(), 1),
    deliveryTime: "9am-12pm",
    specialInstructions: "",
  });

  const [fees, setFees] = useState({
    convenience_fee: 0,
    delivery_fee: 10,
    membership_fee: 0,
    total: 0,
    savings: 0,
    is_member_rate: false,
  });
  const [joinMembership, setJoinMembership] = useState(false);

  // Redirect if cart is empty (after initial load)
  const hasShownEmptyToast = React.useRef(false);
  
  useEffect(() => {
    if (cart.items !== undefined && cart.items.length === 0 && !hasShownEmptyToast.current) {
      hasShownEmptyToast.current = true;
      toast.error("Your cart is empty");
      navigate("/products");
    }
  }, [cart.items, navigate]);

  // Fetch payment config
  useEffect(() => {
    const fetchData = async () => {
      try {
        const paymentRes = await axios.get(`${API}/payment/status`);
        setPaymentConfig(paymentRes.data);
      } catch (error) {
        console.error("Error fetching checkout data:", error);
      }
    };
    fetchData();
  }, []);

  // Fetch store hours
  useEffect(() => {
    const fetchStoreHours = async () => {
      if (!cart.stores) return;
      
      const hours = {};
      for (const store of cart.stores) {
        try {
          const response = await axios.get(`${API}/stores/${store.store_id}/hours`);
          hours[store.store_id] = response.data;
        } catch (error) {
          console.error(`Error fetching hours for ${store.store_id}:`, error);
        }
      }
      setStoreHours(hours);
    };
    fetchStoreHours();
  }, [cart.stores]);

  // Calculate fees when membership toggle or subtotal changes
  useEffect(() => {
    const calculateFees = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.post(`${API}/calculate-fees`, null, {
          params: {
            subtotal: cart.subtotal,
            join_membership: joinMembership,
          },
          headers,
        });
        setFees(response.data);
      } catch (error) {
        console.error("Error calculating fees:", error);
      }
    };
    if (cart.subtotal) {
      calculateFees();
    }
  }, [cart.subtotal, joinMembership, isAuthenticated, isMember, token]);

  // Delivery time slot options
  const timeSlots = [
    { value: "9am-12pm", label: "Morning (9am - 12pm)" },
    { value: "5pm-9pm", label: "Evening (5pm - 9pm)" },
  ];

  // Check if any store closes soon
  const hasStoreClosingSoon = Object.values(storeHours).some((h) => h.closes_soon);

  const [validationErrors, setValidationErrors] = useState({});

  // Track if all required fields are filled for green button
  const isFormComplete = !!(
    formData.email && formData.email.includes('@') &&
    formData.phone && formData.phone.trim().length >= 8 &&
    formData.address && formData.address.trim().length >= 5 &&
    formData.suburb && formData.suburb.trim().length >= 2 &&
    formData.postcode && formData.postcode.trim().length >= 4 &&
    formData.deliveryDate &&
    formData.deliveryTime &&
    cardData.card_name &&
    cardData.card_number &&
    cardData.card_expiry_month &&
    cardData.card_expiry_year &&
    cardData.card_cvn && cardData.card_cvn.length === 3
  );

  const validateForm = () => {
    const errors = {};
    
    if (!formData.fullName || formData.fullName.trim().length < 2) {
      errors.fullName = "Please enter your full name";
    }
    if (!formData.email || !formData.email.includes('@')) {
      errors.email = "Please enter a valid email address";
    }
    if (!formData.phone || formData.phone.trim().length < 8) {
      errors.phone = "Please enter a valid phone number";
    }
    if (!formData.address || formData.address.trim().length < 5) {
      errors.address = "Please enter a valid street address";
    }
    if (!formData.suburb || formData.suburb.trim().length < 2) {
      errors.suburb = "Please enter a valid suburb";
    }
    if (!formData.postcode || formData.postcode.trim().length < 4) {
      errors.postcode = "Please enter a valid postcode";
    }
    if (!formData.deliveryDate) {
      errors.deliveryDate = "Please select a delivery date";
    }
    if (!formData.deliveryTime) {
      errors.deliveryTime = "Please select a delivery time";
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setPaymentError(null);
    
    if (!validateForm()) {
      toast.error("Please fill in all required fields correctly");
      return;
    }

    // Validate card details
    if (!cardData.card_name || !cardData.card_number || !cardData.card_expiry_month || 
        !cardData.card_expiry_year || !cardData.card_cvn) {
      toast.error("Please fill in all card details");
      return;
    }
    
    // CVN must be exactly 3 digits
    if (cardData.card_cvn.length !== 3 || !/^\d{3}$/.test(cardData.card_cvn)) {
      toast.error("CVN must be exactly 3 digits");
      return;
    }

    setLoading(true);
    
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Step 1: Initialize checkout (get eWay Access Code)
      const initData = {
        session_id: sessionId,
        full_name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        delivery_details: {
          name: formData.fullName,
          address: formData.address,
          suburb: formData.suburb,
          postcode: formData.postcode,
          state: formData.state,
          delivery_date: format(formData.deliveryDate, "yyyy-MM-dd"),
          delivery_time: formData.deliveryTime,
          special_instructions: formData.specialInstructions,
        },
        join_membership: joinMembership && !isMember,
      };

      const initResponse = await axios.post(`${API}/checkout/init`, initData, { headers });
      
      if (!initResponse.data.success) {
        setPaymentError({ message: initResponse.data.message || "Failed to initialize payment." });
        toast.error("Failed to initialize payment. Please try again.");
        setLoading(false);
        return;
      }

      // Real-time validation: check if fees changed (e.g., membership expired between page load and order)
      const serverFees = initResponse.data.fees;
      if (serverFees && Math.abs(serverFees.convenience_fee - fees.convenience_fee) > 0.01) {
        setFees({
          convenience_fee: serverFees.convenience_fee,
          delivery_fee: serverFees.delivery_fee,
          membership_fee: serverFees.membership_fee || 0,
          total: serverFees.total,
          savings: serverFees.savings || 0,
          is_member_rate: serverFees.is_member_rate,
        });
        toast.warning("Your membership status has changed. Fees have been updated to the current rate. Please review and try again.");
        setLoading(false);
        return;
      }

      // Step 2: Submit card details directly to eWay via hidden form
      const { access_code, form_action_url } = initResponse.data;
      
      const form = document.createElement("form");
      form.method = "POST";
      form.action = form_action_url;
      form.style.display = "none";

      const fields = {
        EWAY_ACCESSCODE: access_code,
        EWAY_PAYMENTTYPE: "Credit Card",
        EWAY_CARDNAME: cardData.card_name,
        EWAY_CARDNUMBER: cardData.card_number.replace(/\s/g, ""),
        EWAY_CARDEXPIRYMONTH: cardData.card_expiry_month.padStart(2, "0"),
        EWAY_CARDEXPIRYYEAR: cardData.card_expiry_year,
        EWAY_CARDCVN: cardData.card_cvn,
      };

      Object.entries(fields).forEach(([name, value]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = name;
        input.value = value;
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
      // Browser navigates to eWay, then eWay redirects to /checkout/complete
      
    } catch (error) {
      setLoading(false);
      const errorMessage = error.response?.data?.detail || "Failed to process payment. Please try again.";
      setPaymentError({ message: errorMessage });
      toast.error(errorMessage);
    }
  };

  // Disable past dates and more than 7 days in future
  const disabledDays = (date) => {
    const tomorrow = addDays(startOfDay(new Date()), 1);
    const maxDate = addDays(new Date(), 7);
    return isBefore(date, tomorrow) || date > maxDate;
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="checkout-page">
      <div className="container-main py-8">
        <h1 className="text-2xl font-bold text-slate-900 font-['Outfit'] mb-6">
          Checkout
        </h1>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Form */}
            <div className="lg:col-span-2 space-y-6">
              {/* Store Closing Warning */}
              {hasStoreClosingSoon && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">Store Closing Soon</p>
                      <p className="text-sm text-amber-700 mt-1">
                        Some stores in your order are closing within 1 hour. 
                        Delivery will be scheduled for the next available day.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Contact Information */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Contact Information
                </h2>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="fullName">Full Name <span className="text-red-500">*</span></Label>
                    <Input
                      id="fullName"
                      type="text"
                      value={formData.fullName}
                      onChange={(e) => {
                        setFormData({ ...formData, fullName: e.target.value });
                        setValidationErrors((prev) => ({ ...prev, fullName: null }));
                      }}
                      placeholder="John Smith"
                      className={validationErrors.fullName ? "border-red-500" : ""}
                      required
                      data-testid="input-fullname"
                    />
                    {validationErrors.fullName && (
                      <p className="text-xs text-red-500 mt-1">{validationErrors.fullName}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="email">Email Address <span className="text-red-500">*</span></Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => {
                        setFormData({ ...formData, email: e.target.value });
                        setValidationErrors((prev) => ({ ...prev, email: null }));
                      }}
                      className={validationErrors.email ? "border-red-500" : ""}
                      required
                      data-testid="input-email"
                    />
                    {validationErrors.email && (
                      <p className="text-xs text-red-500 mt-1">{validationErrors.email}</p>
                    )}
                    <p className="text-xs text-slate-500 mt-1">
                      Order confirmation will be sent to this email
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number <span className="text-red-500">*</span></Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => {
                        setFormData({ ...formData, phone: e.target.value });
                        setValidationErrors((prev) => ({ ...prev, phone: null }));
                      }}
                      placeholder="04XX XXX XXX"
                      className={validationErrors.phone ? "border-red-500" : ""}
                      required
                      data-testid="input-phone"
                    />
                    {validationErrors.phone && (
                      <p className="text-xs text-red-500 mt-1">{validationErrors.phone}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Delivery Address */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Truck className="w-5 h-5" />
                  Delivery Address
                </h2>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="address">Street Address <span className="text-red-500">*</span></Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => {
                        setFormData({ ...formData, address: e.target.value });
                        setValidationErrors((prev) => ({ ...prev, address: null }));
                      }}
                      placeholder="123 Example Street, Unit 4"
                      className={validationErrors.address ? "border-red-500" : ""}
                      required
                      data-testid="input-address"
                    />
                    {validationErrors.address && (
                      <p className="text-xs text-red-500 mt-1">{validationErrors.address}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="suburb">Suburb <span className="text-red-500">*</span></Label>
                      <Input
                        id="suburb"
                        value={formData.suburb}
                        onChange={(e) => {
                          setFormData({ ...formData, suburb: e.target.value });
                          setValidationErrors((prev) => ({ ...prev, suburb: null }));
                        }}
                        className={validationErrors.suburb ? "border-red-500" : ""}
                        required
                        data-testid="input-suburb"
                      />
                      {validationErrors.suburb && (
                        <p className="text-xs text-red-500 mt-1">{validationErrors.suburb}</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="postcode">Postcode <span className="text-red-500">*</span></Label>
                      <Input
                        id="postcode"
                        value={formData.postcode}
                        onChange={(e) => {
                          setFormData({ ...formData, postcode: e.target.value });
                          setValidationErrors((prev) => ({ ...prev, postcode: null }));
                        }}
                        className={validationErrors.postcode ? "border-red-500" : ""}
                        required
                        data-testid="input-postcode"
                      />
                      {validationErrors.postcode && (
                        <p className="text-xs text-red-500 mt-1">{validationErrors.postcode}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="state">State</Label>
                    <Select
                      value={formData.state}
                      onValueChange={(value) => setFormData({ ...formData, state: value })}
                    >
                      <SelectTrigger data-testid="select-state">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NSW">New South Wales</SelectItem>
                        <SelectItem value="VIC">Victoria</SelectItem>
                        <SelectItem value="QLD">Queensland</SelectItem>
                        <SelectItem value="WA">Western Australia</SelectItem>
                        <SelectItem value="SA">South Australia</SelectItem>
                        <SelectItem value="TAS">Tasmania</SelectItem>
                        <SelectItem value="ACT">Australian Capital Territory</SelectItem>
                        <SelectItem value="NT">Northern Territory</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Delivery */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Truck className="w-5 h-5" />
                  Delivery
                </h2>
                <div className="border rounded-xl p-4 border-sky-500 bg-sky-50">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-sky-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-base font-medium text-slate-900">
                        Delivered to your door
                      </p>
                      <p className="text-sm text-slate-600 mt-1">
                        We collect items from all stores and deliver them to you.
                      </p>
                    </div>
                    <span className="text-slate-900 font-semibold">$10.00</span>
                  </div>
                </div>
              </div>

              {/* Delivery Schedule */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5" />
                  Delivery Schedule
                </h2>

                <p className="text-sm text-slate-600 mb-4">
                  Select your preferred delivery date and time window
                </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label>Delivery Date <span className="text-red-500">*</span></Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={`w-full justify-start text-left font-normal mt-2 ${validationErrors.deliveryDate ? "border-red-500" : ""}`}
                            data-testid="date-picker-trigger"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {formData.deliveryDate ? (
                              format(formData.deliveryDate, "PPP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={formData.deliveryDate}
                            onSelect={(date) => {
                              setFormData({ ...formData, deliveryDate: date });
                              setValidationErrors((prev) => ({ ...prev, deliveryDate: null }));
                            }}
                            disabled={disabledDays}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      {validationErrors.deliveryDate && (
                        <p className="text-xs text-red-500 mt-1">{validationErrors.deliveryDate}</p>
                      )}
                    </div>

                    <div>
                      <Label>Delivery Time <span className="text-red-500">*</span></Label>
                      <Select
                        value={formData.deliveryTime}
                        onValueChange={(value) => {
                          setFormData({ ...formData, deliveryTime: value });
                          setValidationErrors((prev) => ({ ...prev, deliveryTime: null }));
                        }}
                      >
                        <SelectTrigger className={`mt-2 ${validationErrors.deliveryTime ? "border-red-500" : ""}`} data-testid="time-picker">
                          <Clock className="w-4 h-4 mr-2" />
                          <SelectValue placeholder="Select time" />
                        </SelectTrigger>
                        <SelectContent>
                          {timeSlots.map((slot) => (
                            <SelectItem key={slot.value} value={slot.value}>
                              {slot.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {validationErrors.deliveryTime && (
                        <p className="text-xs text-red-500 mt-1">{validationErrors.deliveryTime}</p>
                      )}
                    </div>
                  </div>
                </div>

              {/* Special Instructions */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">
                  Special Instructions (Optional)
                </h2>
                <Textarea
                  placeholder="Any special delivery instructions or notes..."
                  value={formData.specialInstructions}
                  onChange={(e) => setFormData({ ...formData, specialInstructions: e.target.value })}
                  rows={2}
                  maxLength={100}
                  data-testid="special-instructions"
                />
                <p className="text-xs text-slate-400 mt-1 text-right">
                  {formData.specialInstructions.length}/100
                </p>
              </div>
            </div>

            {/* Right Column - Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-sm p-6 sticky top-24">
                <h2 className="text-lg font-bold text-slate-900 font-['Outfit'] mb-4">
                  Order Summary
                </h2>

                {/* Items grouped by store */}
                <div className="max-h-56 overflow-y-auto mb-4 space-y-3">
                  {cart.stores?.map((store, sIdx) => (
                    <div key={sIdx} className="space-y-1">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{store.store_name}</p>
                      {store.items?.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm pl-2">
                          <span className="text-slate-600 truncate max-w-[170px]">
                            {item.quantity}x {item.product_name}
                          </span>
                          <span className="font-medium">${(item.price * item.quantity).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                  {(!cart.stores || cart.stores.length === 0) && cart.items?.map((item, index) => (
                    <div key={index} className="flex items-center justify-between text-sm">
                      <span className="text-slate-600 truncate max-w-[170px]">
                        {item.quantity}x {item.product_name}
                        <span className="text-xs text-slate-400 ml-1">({item.store_name || item.retailer})</span>
                      </span>
                      <span className="font-medium">${(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>

                <Separator className="my-4" />

                {/* Join & Save Toggle - Only for non-members */}
                {isAuthenticated && !isMember && (
                  <div
                    className={`mb-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      joinMembership
                        ? "border-emerald-500 bg-emerald-50"
                        : "border-slate-200 bg-slate-50 hover:border-sky-300"
                    }`}
                    onClick={() => setJoinMembership(!joinMembership)}
                    data-testid="join-membership-toggle"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        joinMembership ? "bg-emerald-500 border-emerald-500" : "border-slate-300"
                      }`}>
                        {joinMembership && <CheckCircle className="w-3.5 h-3.5 text-white" />}
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-slate-900 text-sm">
                          Upgrade now for $5.00
                        </p>
                        <p className="text-xs text-slate-600 mt-0.5">
                          Your Convenience Fee will drop from 7% to 4% immediately for this order!
                        </p>
                      </div>
                    </div>
                    {joinMembership && fees.savings > 0 && (
                      <p className="text-xs text-emerald-700 font-medium mt-2 ml-8">
                        You save ${fees.savings.toFixed(2)} on this order
                      </p>
                    )}
                  </div>
                )}

                {/* Fee Breakdown */}
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Subtotal</span>
                    <span className="font-medium">${cart.subtotal?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600 flex items-center gap-1">
                      Convenience Fee ({fees.is_member_rate ? "4%" : "7%"})
                      {fees.is_member_rate && <Crown className="w-3 h-3 text-sky-600" />}
                    </span>
                    <span className="font-medium">${fees.convenience_fee?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Delivery</span>
                    <span className="font-medium">${fees.delivery_fee?.toFixed(2)}</span>
                  </div>
                  {fees.membership_fee > 0 && (
                    <div className="flex justify-between text-sm text-emerald-700">
                      <span className="flex items-center gap-1">
                        <Crown className="w-3 h-3" /> Membership
                      </span>
                      <span className="font-medium">${fees.membership_fee?.toFixed(2)}</span>
                    </div>
                  )}

                  <Separator />

                  <div className="flex justify-between text-lg">
                    <span className="font-bold">Total</span>
                    <span className="font-bold">${fees.total?.toFixed(2)}</span>
                  </div>
                  
                  {/* Fee Disclaimer */}
                  <div className="p-2 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-xs text-amber-800">
                      <strong>Fee Info:</strong> 4% for Members, 7% for Guests (max $15). Delivery: $10.
                    </p>
                  </div>
                </div>

                {/* Member Badge */}
                {isMember && (
                  <div className="mt-4 p-3 bg-sky-50 rounded-lg flex items-center gap-2">
                    <Crown className="w-4 h-4 text-sky-600" />
                    <span className="text-sm text-sky-800 font-medium">
                      Member discount applied (4%)
                    </span>
                  </div>
                )}

                {/* Payment Error Display */}
                {paymentError && (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <CreditCard className="w-4 h-4 text-red-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-red-800">Payment Failed</h4>
                        <p className="text-sm text-red-700 mt-1">{paymentError.message}</p>
                        {paymentError.details && paymentError.details !== paymentError.message && (
                          <p className="text-xs text-red-600 mt-1">{paymentError.details}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Card Payment Form - Only payment method */}
                <div className="mt-6 p-4 bg-slate-50 rounded-lg space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard className="w-4 h-4 text-slate-600" />
                    <span className="font-medium text-slate-900">Card Payment</span>
                    {paymentConfig?.eway_sandbox && (
                      <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
                        Test Mode
                      </span>
                    )}
                  </div>
                  
                  {paymentConfig?.eway_sandbox && paymentConfig?.test_card && (
                    <div className="text-xs bg-yellow-50 border border-yellow-200 rounded p-2 mb-3">
                      <strong>Test Card:</strong> {paymentConfig.test_card.number} | 
                      Exp: {paymentConfig.test_card.expiry} | CVN: {paymentConfig.test_card.cvn}
                    </div>
                  )}
                  
                  <div>
                    <Label className="text-xs text-slate-600">Name on Card <span className="text-red-500">*</span></Label>
                    <Input
                      placeholder="John Smith"
                      value={cardData.card_name}
                      onChange={(e) => setCardData({...cardData, card_name: e.target.value})}
                      className="mt-1"
                      data-testid="card-name"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label className="text-xs text-slate-600">Card Number <span className="text-red-500">*</span></Label>
                    <Input
                      placeholder="4444 3333 2222 1111"
                      value={cardData.card_number}
                      onChange={(e) => setCardData({...cardData, card_number: e.target.value})}
                      className="mt-1"
                      maxLength={19}
                      data-testid="card-number"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs text-slate-600 h-5 flex items-center">Month <span className="text-red-500 ml-0.5">*</span></Label>
                      <Input
                        placeholder="12"
                        value={cardData.card_expiry_month}
                        onChange={(e) => setCardData({...cardData, card_expiry_month: e.target.value})}
                        className="mt-1"
                        maxLength={2}
                        data-testid="card-month"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600 h-5 flex items-center">Year <span className="text-red-500 ml-0.5">*</span></Label>
                      <Input
                        placeholder="28"
                        value={cardData.card_expiry_year}
                        onChange={(e) => setCardData({...cardData, card_expiry_year: e.target.value})}
                        className="mt-1"
                        maxLength={2}
                        data-testid="card-year"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600 h-5 flex items-center gap-1">
                        CVN <span className="text-red-500">*</span>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <HelpCircle className="w-3.5 h-3.5 text-slate-400 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent side="top" className="text-xs">
                              <p>The 3-digit code on the back of your card.</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </Label>
                      <Input
                        placeholder="3 digits"
                        value={cardData.card_cvn}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 3);
                          setCardData({...cardData, card_cvn: val});
                        }}
                        className="mt-1"
                        maxLength={3}
                        type="tel"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        data-testid="card-cvn"
                        required
                      />
                    </div>
                  </div>
                  
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <Info className="w-3 h-3" />
                    Card details are sent directly to the Payment Gateway - never stored on our servers
                  </p>
                </div>

                <Button
                  type="submit"
                  className={`w-full mt-6 h-12 font-semibold text-base transition-colors ${
                    isFormComplete
                      ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                      : "bg-slate-300 text-slate-500 cursor-not-allowed"
                  }`}
                  disabled={loading}
                  data-testid="place-order-btn"
                >
                  {loading ? "Processing Payment..." : "Pay & Place Order"}
                  <CheckCircle className="w-4 h-4 ml-2" />
                </Button>

                <p className="text-xs text-slate-500 text-center mt-3">
                  Your order will be processed immediately upon successful payment{fees.membership_fee > 0 ? " (includes $5 membership)" : ""}.
                </p>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CheckoutPage;
